import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { HardDrive } from "lucide-react";
import { getStorageEstimate } from "@/lib/db";
import { useI18n } from "@/lib/i18n";
import { useAppData } from "@/hooks/useAppData";

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 KB';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const StorageBar = () => {
  const { t } = useI18n();
  const { dataItems } = useAppData();
  const [used, setUsed] = useState(0);
  const [quota, setQuota] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  useEffect(() => {
    getStorageEstimate().then(({ used, quota }) => {
      setUsed(used);
      setQuota(quota);
    });
  }, [dataItems]);

  const percent = quota > 0 ? Math.min((used / quota) * 100, 100) : 0;
  
  const getStatusColor = () => {
    if (percent > 90) return "bg-destructive";
    if (percent > 70) return "bg-yellow-500";
    return "bg-primary";
  };

  const getStatusKey = () => {
    if (percent > 90) return "storage.status_critical";
    if (percent > 70) return "storage.status_warning";
    return "storage.status_safe";
  };

  return (
    <div className="relative group">
      {/* Knowledge Tooltip */}
      {isHovered && (
        <motion.div
          initial={{ opacity: 0, y: 5, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          className="absolute bottom-full left-0 mb-3 w-64 p-3 rounded-lg glass-bar border border-primary/20 shadow-xl z-50 text-[11px] leading-relaxed text-muted-foreground"
        >
          <div className="flex items-start gap-2">
            <div className="w-1 h-1 rounded-full bg-primary mt-1.5 shrink-0" />
            <p>{t('storage.guide')}</p>
          </div>
          {percent > 70 && (
            <div className="mt-2 pt-2 border-t border-primary/10 font-semibold text-primary">
              {t(getStatusKey())}
            </div>
          )}
        </motion.div>
      )}

      <motion.div
        initial={{ y: 10, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className="glass-bar rounded-xl px-4 py-1.5 flex flex-col gap-1 hover:bg-primary/5 transition-colors cursor-help"
      >
        <div className="flex items-center gap-3">
          <HardDrive className={`w-3.5 h-3.5 ${percent > 90 ? 'text-destructive animate-pulse' : 'text-muted-foreground'}`} />
          <div className="flex-1 relative flex items-center">
            <div 
              className="w-full h-2 rounded-full bg-background/50 overflow-hidden"
              role="progressbar"
              aria-valuenow={Math.round(percent)}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${Math.max(percent, 1)}%` }}
                className={`h-full rounded-full transition-colors duration-500 ${getStatusColor()}`}
              />
            </div>
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <span className="text-[9px] font-bold text-foreground drop-shadow-sm whitespace-nowrap">
                {formatBytes(used)} / {formatBytes(quota)} ({percent.toFixed(1)}%)
              </span>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default StorageBar;
